import { ReactNode, useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Pencil,
  Search,
  SlidersHorizontal,
  X,
  ArrowUpDown,
  Loader2,
} from 'lucide-react';
import { AppState } from '../../store';
import {
  InventoryItem,
  InventoryType,
  InventoryStatus,
  InventoryStatusMap,
} from '../../modules/inventory/types';
import { getDateString } from '../../general/dates';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from 'components/ui/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';

// Fields already supplied by the inventory selectors, including optional legacy values.
export type InventoryRow = InventoryItem & {
  serial?: string;
  identifier?: string;
  deviceId?: string;
  iccid?: string;
  phoneNumber?: string;
  orgId?: string;
  orgTitle?: string;
  sim?: string | null;
  simcard?: { iccid?: string };
  price?: number | string;
  tags?: string[];
  isActive?: boolean;
  lastSeen?: string;
  connection?: { title?: string; type?: InventoryType };
};
interface Props {
  data: InventoryRow[];
  type: InventoryType;
  setVisible: (id: string) => void;
  inventoryTagsMap?: Record<string, { title: string; color?: string }>;
  simcardMap?: Record<string, { iccid: string }>;
  onDuplicate?: (row: InventoryRow) => Promise<void>;
}

const statusColors: Partial<Record<InventoryStatus, string>> = {
  [InventoryStatus.NEEDS_ORDER]: 'border-rose-200 bg-rose-50 text-rose-700',
  [InventoryStatus.ISSUE]: 'border-rose-200 bg-rose-50 text-rose-700',
  [InventoryStatus.ORDERED]: 'border-amber-200 bg-amber-50 text-amber-800',
  [InventoryStatus.IN_STORAGE]: 'border-orange-200 bg-orange-50 text-orange-800',
  [InventoryStatus.READY]: 'border-blue-200 bg-blue-50 text-blue-700',
  [InventoryStatus.ASSIGNED]: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  [InventoryStatus.RETRIEVE]: 'border-cyan-200 bg-cyan-50 text-cyan-800',
};

export default function InventoryTable(props: Props) {
  const uid = useSelector((state: AppState) => state.user.details.uid);
  const { search } = useLocation();
  const iccid =
    new URLSearchParams(search).get('id') ??
    (props.type === InventoryType.SIM_CARDS ? new URLSearchParams(search).get('iccid') : null);
  return (
    <InventoryList
      key={`${uid}:${props.type}:${iccid ?? ''}`}
      {...props}
      uid={uid}
      initialSearch={iccid}
    />
  );
}

function InventoryList({
  data,
  type,
  setVisible,
  inventoryTagsMap = {},
  simcardMap = {},
  onDuplicate,
  uid,
  initialSearch,
}: Props & { uid: string; initialSearch: string | null }) {
  const { t } = useTranslation();
  const intl = useSelector((state: AppState) => state.user.userDoc?.intl);
  const loading = useSelector((state: AppState) => state.inventory?.loading) && !data.length;
  const history = useHistory();
  const location = useLocation();
  const storageKey = `inventory:last-search:${uid}:${type}`;
  const [query, setQuery] = useState(() => {
    if (initialSearch !== null) return initialSearch;
    try {
      return localStorage.getItem(storageKey) || '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    if (initialSearch !== null) {
      try {
        localStorage.setItem(storageKey, initialSearch);
      } catch {
        /* Storage is optional. */
      }
    }
  }, [initialSearch, storageKey]);
  const [fieldFilters, setFieldFilters] = useState<Record<string, string>>({});
  const fieldFilterCount = Object.values(fieldFilters).filter((value) => value.trim()).length;
  const [statuses, setStatuses] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [activitySort, setActivitySort] = useState<'asc' | 'desc' | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const isSim = type === InventoryType.SIM_CARDS;
  const hasConnection = type === InventoryType.ESP_AQ;
  const isSensor = hasConnection || type === InventoryType.MACHINE_ACTIVITY;
  const isGPS = type === InventoryType.GPS;
  const hasTags = isSim || isSensor;
  const hasSim = isGPS || isSensor || type === InventoryType.IPAD;
  const hasName = !isSim && !isSensor;
  const hasNote = isSim || isSensor || isGPS;
  const searchFields: { key: keyof InventoryRow; label: string }[] = [
    {
      key: isSim
        ? 'iccid'
        : isGPS
        ? 'identifier'
        : isSensor || type === InventoryType.FOOBOT
        ? 'deviceId'
        : 'serial',
      label: isSim ? 'ICCID' : isGPS ? 'inventory.gps.serial' : 'inventory.ipad.serial',
    },
    ...(hasName ? [{ key: 'name' as const, label: 'inventory.ipad.name' }] : []),
    ...(hasSim ? [{ key: 'sim' as const, label: 'inventory.ipad.sim' }] : []),
    ...(!isSim ? [{ key: 'orgTitle' as const, label: 'inventory.ipad.org' }] : []),
    ...(type === InventoryType.PUMP ? [{ key: 'price' as const, label: 'Pris' }] : []),
    ...(hasNote ? [{ key: 'note' as const, label: 'Note' }] : []),
  ];
  const identifier = (row: InventoryRow) =>
    (isSim
      ? row.iccid
      : isGPS
      ? row.identifier
      : isSensor || type === InventoryType.FOOBOT
      ? row.deviceId
      : row.serial) || row.id;
  const simNumber = (row: InventoryRow) =>
    row.sim ? simcardMap[row.sim]?.iccid || row.simcard?.iccid || row.sim : '';
  const updateQuery = (value: string) => {
    setQuery(value);
    setPage(1);
    try {
      value ? localStorage.setItem(storageKey, value) : localStorage.removeItem(storageKey);
    } catch {
      /* Search still works when storage is unavailable. */
    }
    if (initialSearch !== null) {
      const params = new URLSearchParams(location.search);
      params.delete('iccid');
      params.delete('id');
      history.replace({ ...location, search: params.toString() ? `?${params}` : '' });
    }
  };
  const toggle = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
  const queryText = query.trim().toLocaleLowerCase();
  const filtered = data.filter((row) => {
    const terms = [
      identifier(row),
      row.id,
      row.internalId,
      row.phoneNumber,
      row.name,
      row.orgTitle,
      simNumber(row),
      row.note,
      row.price,
      row.connection?.title,
      ...(row.tags || []).map((tag) => inventoryTagsMap[tag]?.title || tag),
    ];
    return (
      (!queryText ||
        terms.some((value) =>
          String(value ?? '')
            .toLocaleLowerCase()
            .includes(queryText)
        )) &&
      Object.entries(fieldFilters).every(([key, value]) =>
        String((key === 'sim' ? simNumber(row) : row[key as keyof InventoryRow]) ?? '')
          .toLocaleLowerCase()
          .includes(value.trim().toLocaleLowerCase())
      ) &&
      (!statuses.length || statuses.includes(String(row.status))) &&
      (!tags.length || (row.tags || []).some((tag) => tags.includes(tag)))
    );
  });
  if (activitySort)
    filtered.sort(
      (a, b) =>
        (Number(Boolean(a.isActive)) - Number(Boolean(b.isActive))) *
        (activitySort === 'asc' ? 1 : -1)
    );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * pageSize;
  const counts = data.reduce<Record<string, number>>((result, row) => {
    const keys = isSim ? row.tags || [] : [String(row.status)];
    for (const key of keys) result[key] = (result[key] || 0) + 1;
    return result;
  }, {});
  const reset = () => {
    setFieldFilters({});
    setStatuses([]);
    setTags([]);
    updateQuery('');
  };
  const cell = (content: ReactNode) => (
    <span className="block max-w-[240px] whitespace-normal break-words">{content || '—'}</span>
  );
  const columnCount =
    4 +
    Number(hasConnection) +
    Number(hasName) +
    Number(hasSim) +
    Number(type === InventoryType.PUMP) +
    Number(hasTags) +
    Number(hasNote) +
    Number(!isSim);

  return (
    <div className="space-y-4">
      <div aria-label={t('Lagerstatus')} className="flex flex-wrap items-center gap-2">
        {Object.entries(counts).map(([key, count]) => {
          const label = isSim
            ? inventoryTagsMap[key]?.title || key
            : InventoryStatusMap[Number(key) as InventoryStatus];
          if (!label) return null;
          const selected = (isSim ? tags : statuses).includes(key);
          return (
            <Button
              key={key}
              size="sm"
              variant={selected ? 'secondary' : 'ghost'}
              className="h-7 gap-2 px-2 text-xs"
              aria-pressed={selected}
              onClick={() => {
                isSim ? setTags(toggle(tags, key)) : setStatuses(toggle(statuses, key));
                setPage(1);
              }}
            >
              <span className="text-muted-foreground">{t(label)}</span>
              <span className="tabular-nums">{count}</span>
            </Button>
          );
        })}
      </div>
      <section
        aria-label={t('Lagerliste')}
        aria-busy={Boolean(loading)}
        className="overflow-hidden rounded-xl border bg-background shadow-sm"
      >
        <div className="flex flex-wrap items-center gap-3 border-b p-4">
          <div className="relative min-w-[200px] flex-1 sm:max-w-md">
            <Search
              aria-hidden="true"
              className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"
            />
            <Input
              aria-label={t('Søg i lager')}
              placeholder={t('Søg efter udstyr, afdeling, SIM eller note…')}
              value={query}
              onChange={(event) => updateQuery(event.target.value)}
              className="pl-9 pr-9"
            />
            {query && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-8 w-8"
                aria-label={t('Ryd søgning')}
                onClick={() => updateQuery('')}
              >
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
          {!isSim && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <SlidersHorizontal aria-hidden="true" />
                  {t('Lagerstatus')}
                  {statuses.length > 0 && <Badge variant="secondary">{statuses.length}</Badge>}
                  <ChevronDown aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>{t('Filtrer efter status')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.entries(InventoryStatusMap).map(([key, label]) => (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={statuses.includes(key)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={() => {
                      setStatuses(toggle(statuses, key));
                      setPage(1);
                    }}
                  >
                    {t(label)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {hasTags && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t('Tags')}
                  {tags.length > 0 && <Badge variant="secondary">{tags.length}</Badge>}
                  <ChevronDown aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>{t('Filtrer efter tag')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.entries(inventoryTagsMap).map(([key, tag]) => (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={tags.includes(key)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={() => {
                      setTags(toggle(tags, key));
                      setPage(1);
                    }}
                  >
                    {tag.title}
                  </DropdownMenuCheckboxItem>
                ))}
                {!Object.keys(inventoryTagsMap).length && (
                  <p className="px-2 text-sm text-muted-foreground">{t('Ingen tags')}</p>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">
                {t('Flere filtre')}
                {fieldFilterCount > 0 && <Badge variant="secondary">{fieldFilterCount}</Badge>}
                <ChevronDown aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="max-h-[70vh] w-80 overflow-y-auto">
              <h2 className="mb-3 text-sm font-semibold">{t('Søg i bestemte felter')}</h2>
              <div className="space-y-3">
                {searchFields.map(({ key, label }) => (
                  <label key={key} className="block space-y-1 text-xs text-muted-foreground">
                    <span>{t(label)}</span>
                    <Input
                      value={fieldFilters[key] || ''}
                      onChange={(event) => {
                        setFieldFilters({ ...fieldFilters, [key]: event.target.value });
                        setPage(1);
                      }}
                    />
                  </label>
                ))}
              </div>
              {fieldFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setFieldFilters({});
                    setPage(1);
                  }}
                >
                  {t('Ryd feltfiltre')}
                </Button>
              )}
            </PopoverContent>
          </Popover>
          {(query || fieldFilterCount > 0 || statuses.length > 0 || tags.length > 0) && (
            <Button variant="ghost" size="sm" onClick={reset}>
              {t('Nulstil filtre')}
            </Button>
          )}
          <span role="status" className="ml-auto text-xs text-muted-foreground">
            {t('{{count}} af {{total}}', { count: filtered.length, total: data.length })}
          </span>
        </div>
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="whitespace-nowrap">
              {hasConnection && (
                <TableHead
                  className="pl-4"
                  aria-sort={
                    activitySort === 'asc'
                      ? 'ascending'
                      : activitySort === 'desc'
                      ? 'descending'
                      : 'none'
                  }
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setActivitySort(
                        activitySort === null ? 'asc' : activitySort === 'asc' ? 'desc' : null
                      )
                    }
                  >
                    {t('Forbindelse')}
                    <ArrowUpDown aria-hidden="true" />
                  </Button>
                </TableHead>
              )}
              <TableHead className="pl-4">
                {t(isSim ? 'ICCID' : isGPS ? 'inventory.gps.serial' : 'inventory.ipad.serial')}
              </TableHead>
              {hasName && <TableHead>{t('inventory.ipad.name')}</TableHead>}
              {hasSim && <TableHead>{t('inventory.ipad.sim')}</TableHead>}
              {type === InventoryType.PUMP && <TableHead>{t('Pris')}</TableHead>}
              <TableHead>{t(isSim ? 'Tilsluttet udstyr' : 'inventory.ipad.org')}</TableHead>
              {!isSim && <TableHead>{t('Lagerstatus')}</TableHead>}
              {hasTags && <TableHead>{t('Tags')}</TableHead>}
              {hasNote && <TableHead>{t('Note')}</TableHead>}
              <TableHead>{t('inventory.ipad.updatetime')}</TableHead>
              <TableHead className="pr-4 text-right">{t('Handlinger')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.slice(start, start + pageSize).map((row) => (
              <TableRow key={row.id}>
                {hasConnection && (
                  <TableCell className="pl-4">
                    <span
                      title={`${t('Sidst set')}: ${row.lastSeen || '—'}`}
                      className="flex items-center gap-2 whitespace-nowrap text-xs"
                    >
                      <span
                        aria-hidden="true"
                        className={`h-2 w-2 rounded-full ${
                          row.isActive ? 'bg-emerald-500' : 'bg-rose-500'
                        }`}
                      />
                      {t(row.isActive ? 'Aktiv' : 'Inaktiv')}
                    </span>
                  </TableCell>
                )}
                <TableCell className="py-3 pl-4 font-medium">
                  <button
                    className="text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`${t('Ret')}: ${identifier(row)}`}
                    onClick={() => setVisible(row.id)}
                  >
                    {identifier(row)}
                  </button>
                  {row.internalId && (
                    <div className="text-xs text-muted-foreground">
                      Internt ID: {row.internalId}
                    </div>
                  )}
                  {isSim && (
                    <div className="text-xs text-muted-foreground">
                      Mobil: {row.phoneNumber || 'Ikke registreret'}
                    </div>
                  )}
                </TableCell>
                {hasName && <TableCell>{cell(row.name)}</TableCell>}
                {hasSim && (
                  <TableCell>
                    {cell(
                      (isGPS || isSensor) &&
                        (row.simcard?.iccid || (row.sim && simcardMap[row.sim]?.iccid)) ? (
                        <Link
                          className="text-primary hover:underline"
                          to={`/inventory/simcards?iccid=${encodeURIComponent(simNumber(row))}`}
                        >
                          {simNumber(row)}
                        </Link>
                      ) : (
                        simNumber(row)
                      )
                    )}
                  </TableCell>
                )}
                {type === InventoryType.PUMP && (
                  <TableCell className="tabular-nums">{row.price ?? '—'}</TableCell>
                )}
                <TableCell>
                  {cell(
                    isSim ? (
                      row.connection?.type ? (
                        <Link
                          className="text-primary hover:underline"
                          to={`/inventory/${row.connection.type}`}
                        >
                          {row.connection.title || row.connection.type}
                        </Link>
                      ) : null
                    ) : isSensor && row.orgId ? (
                      <Link
                        className="text-primary hover:underline"
                        to={`/orgs/${row.orgId}/sensors`}
                      >
                        {row.orgTitle || row.orgId}
                      </Link>
                    ) : (
                      row.orgTitle
                    )
                  )}
                </TableCell>
                {!isSim && (
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`whitespace-nowrap font-normal ${
                        statusColors[row.status] || 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {t(InventoryStatusMap[row.status] || 'Ukendt')}
                    </Badge>
                  </TableCell>
                )}
                {hasTags && (
                  <TableCell>
                    <div className="flex min-w-[80px] flex-wrap gap-1">
                      {(row.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="font-normal">
                          {inventoryTagsMap[tag]?.color && (
                            <span
                              aria-hidden="true"
                              className="mr-1.5 h-2 w-2 rounded-full"
                              style={{ backgroundColor: inventoryTagsMap[tag].color }}
                            />
                          )}
                          {inventoryTagsMap[tag]?.title || tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                )}
                {hasNote && <TableCell>{cell(row.note)}</TableCell>}
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {row.updateTime ? getDateString(row.updateTime, intl) : '—'}
                </TableCell>
                <TableCell className="pr-4">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`${t('Rediger')}: ${identifier(row)}`}
                      onClick={() => setVisible(row.id)}
                    >
                      <Pencil aria-hidden="true" />
                      {t('Ret')}
                    </Button>
                    {onDuplicate && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={duplicating !== null}
                        aria-label={`${t('Dupliker')}: ${identifier(row)}`}
                        onClick={async () => {
                          if (duplicating) return;
                          setDuplicating(row.id);
                          try {
                            await onDuplicate(row);
                          } finally {
                            setDuplicating(null);
                          }
                        }}
                      >
                        <Copy aria-hidden="true" />
                        {t(duplicating === row.id ? 'Kopierer…' : 'Dupliker')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-40 text-center">
                  <p className="mb-1 font-medium">
                    {loading && (
                      <Loader2 aria-hidden="true" className="mx-auto mb-2 h-5 w-5 animate-spin" />
                    )}
                    {t(
                      loading
                        ? 'Henter lager…'
                        : data.length
                        ? 'Ingen resultater'
                        : 'Intet udstyr endnu'
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      loading
                        ? 'Vent et øjeblik.'
                        : data.length
                        ? 'Prøv en anden søgning eller nulstil dine filtre.'
                        : 'Tilføj udstyr for at komme i gang.'
                    )}
                  </p>
                  {(query || fieldFilterCount > 0 || statuses.length > 0 || tags.length > 0) && (
                    <Button variant="outline" size="sm" onClick={reset}>
                      {t('Nulstil filtre')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground">
          <span>
            {t('Viser {{start}}–{{end}} af {{total}}', {
              start: filtered.length ? start + 1 : 0,
              end: Math.min(start + pageSize, filtered.length),
              total: filtered.length,
            })}
          </span>
          <div className="flex items-center gap-3">
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              aria-label={t('Rækker pr. side')}
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {t('{{count}} pr. side', { count: size })}
                </option>
              ))}
            </select>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9"
              aria-label={t('Forrige side')}
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <span className="tabular-nums">
              {currentPage} / {pages}
            </span>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9"
              aria-label={t('Næste side')}
              disabled={currentPage === pages}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
